/**
 * Artifacts Page
 *
 * CRUD interface for managing artifacts in the BRUTAL config database.
 * Uses EITELKEIT tokens for styling.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { tokens } from '../local-components';
import {
  useAgent,
  type ConfigArtifact,
  type ArtifactCreateInput,
  type ArtifactUpdateInput,
} from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

type ArtifactType =
  | 'ssh'
  | 'docker-compose'
  | 'docker'
  | 'npm'
  | 'terraform'
  | 'static'
  | 'helm'
  | 'cloudflare-dns'
  | 'custom';

const ARTIFACT_TYPES: ArtifactType[] = [
  'ssh',
  'docker-compose',
  'docker',
  'npm',
  'terraform',
  'static',
  'helm',
  'cloudflare-dns',
  'custom',
];

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const buttonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const buttonSecondaryStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: tokens.colors.textPrimary,
  border: `1px solid ${tokens.colors.border}`,
};

const buttonDangerStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.danger,
  color: tokens.colors.textPrimary,
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.surface,
  color: tokens.colors.textMuted,
  cursor: 'not-allowed',
};

const tableContainerStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  overflow: 'hidden',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  borderBottom: `1px solid ${tokens.colors.border}`,
  backgroundColor: tokens.colors.void,
};

const tdStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
  borderBottom: `1px solid ${tokens.colors.border}`,
};

const typeBadgeStyle = (type: string): CSSProperties => {
  const colors: Record<string, string> = {
    'docker-compose': tokens.colors.info,
    docker: tokens.colors.info,
    ssh: tokens.colors.mint,
    terraform: '#7b42bc',
    helm: '#0f1689',
    npm: '#cb3837',
    static: tokens.colors.textMuted,
    'cloudflare-dns': '#f48120',
    custom: tokens.colors.warning,
  };
  const color = colors[type] ?? tokens.colors.textMuted;

  return {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.medium,
    color: color,
    backgroundColor: `${color}22`,
    padding: `2px ${tokens.spacing[2]}`,
    borderRadius: tokens.radii.sm,
  };
};

const actionButtonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  color: tokens.colors.textMuted,
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[4],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

const emptyStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  textAlign: 'center',
  padding: tokens.spacing[8],
};

// Modal styles
const modalOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[6],
  width: '500px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[4],
};

const modalTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const formGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[2],
};

const labelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inputStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
  outline: 'none',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  minHeight: '100px',
  resize: 'vertical',
};

const modalActionsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[2],
  justifyContent: 'flex-end',
  marginTop: tokens.spacing[2],
};

// ═══════════════════════════════════════════════════════════════
// MODAL COMPONENTS
// ═══════════════════════════════════════════════════════════════

interface ArtifactFormProps {
  artifact: ConfigArtifact | null;
  onSave: (data: ArtifactCreateInput | ArtifactUpdateInput, isEdit: boolean) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}

function ArtifactForm({ artifact, onSave, onClose, isSaving }: ArtifactFormProps) {
  const isEdit = artifact !== null;
  const [name, setName] = useState(artifact?.name ?? '');
  const [type, setType] = useState<ArtifactType>(artifact?.type ?? 'docker-compose');
  const [version, setVersion] = useState(artifact?.version ?? '1.0.0');
  const [handler, setHandler] = useState(artifact?.handler ?? '');
  const [configJson, setConfigJson] = useState(
    artifact?.config ? JSON.stringify(artifact.config, null, 2) : '{}',
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    let config: Record<string, unknown>;
    try {
      config = JSON.parse(configJson);
    } catch {
      setError('Invalid JSON in config field');
      return;
    }

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (isEdit) {
      const updates: ArtifactUpdateInput = {
        version: version !== artifact.version ? version : null,
        handler: handler !== artifact.handler ? handler || null : null,
        dependencies: null,
        config: JSON.stringify(config) !== JSON.stringify(artifact.config) ? config : null,
        deployment: null,
        metadata: null,
      };
      await onSave(updates, true);
    } else {
      const data: ArtifactCreateInput = {
        name: name.trim(),
        type,
        version,
        handler: handler || null,
        dependencies: null,
        config,
        deployment: null,
        metadata: null,
      };
      await onSave(data, false);
    }
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>{isEdit ? 'Edit Artifact' : 'Create Artifact'}</h3>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="my-artifact"
            style={inputStyle}
            disabled={isEdit}
            data-testid="artifact-name-input"
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ArtifactType)}
            style={selectStyle}
            disabled={isEdit}
            data-testid="artifact-type-select"
          >
            {ARTIFACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Version</label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.0.0"
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Handler (optional)</label>
          <input
            type="text"
            value={handler}
            onChange={(e) => setHandler(e.target.value)}
            placeholder="@brutal/handler-docker-compose"
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Config (JSON)</label>
          <textarea
            value={configJson}
            onChange={(e) => setConfigJson(e.target.value)}
            placeholder="{}"
            style={textareaStyle}
          />
        </div>

        {error !== null && <div style={errorStyle}>{error}</div>}

        <div style={modalActionsStyle}>
          <button
            onClick={onClose}
            style={buttonSecondaryStyle}
            disabled={isSaving}
            data-testid="artifact-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={isSaving ? buttonDisabledStyle : buttonStyle}
            disabled={isSaving}
            data-testid="artifact-save-btn"
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeleteConfirmProps {
  artifactName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  isDeleting: boolean;
}

function DeleteConfirm({ artifactName, onConfirm, onClose, isDeleting }: DeleteConfirmProps) {
  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>Delete Artifact</h3>
        <p
          style={{
            fontFamily: tokens.fonts.sans,
            fontSize: tokens.fontSizes.sm,
            color: tokens.colors.textPrimary,
          }}
        >
          Are you sure you want to delete <strong>{artifactName}</strong>? This action cannot be
          undone.
        </p>
        <div style={modalActionsStyle}>
          <button
            onClick={onClose}
            style={buttonSecondaryStyle}
            disabled={isDeleting}
            data-testid="cancel-delete"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={isDeleting ? buttonDisabledStyle : buttonDangerStyle}
            disabled={isDeleting}
            data-testid="confirm-delete"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Artifacts() {
  const { isConnected, listArtifacts, createArtifact, updateArtifact, deleteArtifact } = useAgent();
  const [artifacts, setArtifacts] = useState<ConfigArtifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingArtifact, setEditingArtifact] = useState<ConfigArtifact | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchArtifacts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listArtifacts();
      setArtifacts(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch artifacts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchArtifacts();
    }
  }, [isConnected]);

  const handleCreate = () => {
    setEditingArtifact(null);
    setShowForm(true);
  };

  const handleEdit = (artifact: ConfigArtifact) => {
    setEditingArtifact(artifact);
    setShowForm(true);
  };

  const handleSave = async (data: ArtifactCreateInput | ArtifactUpdateInput, isEdit: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit && editingArtifact !== null) {
        const result = await updateArtifact(editingArtifact.name, data as ArtifactUpdateInput);
        if (!result.success) {
          setError(result.error ?? 'Update failed');
          setIsSaving(false);
          return;
        }
      } else {
        const result = await createArtifact(data as ArtifactCreateInput);
        if (!result.success) {
          setError(result.error ?? 'Create failed');
          setIsSaving(false);
          return;
        }
      }
      setShowForm(false);
      setEditingArtifact(null);
      await fetchArtifacts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteTarget === null) return;
    setIsDeleting(true);
    try {
      const result = await deleteArtifact(deleteTarget);
      if (!result.success) {
        setError(result.error ?? 'Delete failed');
      } else {
        setDeleteTarget(null);
        await fetchArtifacts();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingArtifact(null);
  };

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Artifacts</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Artifacts</h1>
        <div style={loadingStyle}>Loading artifacts...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Artifacts</h1>
        <div style={{ display: 'flex', gap: tokens.spacing[2] }}>
          <button onClick={fetchArtifacts} style={buttonSecondaryStyle}>
            Refresh
          </button>
          <button onClick={handleCreate} style={buttonStyle} data-testid="create-artifact-btn">
            + Create Artifact
          </button>
        </div>
      </div>

      {/* Error */}
      {error !== null && <div style={errorStyle}>Error: {error}</div>}

      {/* Artifacts Table */}
      {artifacts.length === 0 ? (
        <div style={emptyStyle}>No artifacts defined. Click "Create Artifact" to add one.</div>
      ) : (
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Version</th>
                <th style={thStyle}>Handler</th>
                <th style={thStyle}>Updated</th>
                <th style={{ ...thStyle, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artifacts.map((artifact) => (
                <tr key={artifact.id} data-testid="artifact-row">
                  <td style={tdStyle} data-testid="artifact-name">
                    {artifact.name}
                  </td>
                  <td style={tdStyle}>
                    <span style={typeBadgeStyle(artifact.type)}>{artifact.type}</span>
                  </td>
                  <td style={tdStyle}>{artifact.version}</td>
                  <td
                    style={{
                      ...tdStyle,
                      color: artifact.handler ? tokens.colors.textPrimary : tokens.colors.textMuted,
                    }}
                  >
                    {artifact.handler ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, color: tokens.colors.textMuted }}>
                    {new Date(artifact.updatedAt).toLocaleDateString()}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleEdit(artifact)}
                      style={actionButtonStyle}
                      data-testid={`edit-artifact-${artifact.name}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(artifact.name)}
                      style={{ ...actionButtonStyle, color: tokens.colors.danger }}
                      data-testid={`delete-artifact-${artifact.name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <ArtifactForm
          artifact={editingArtifact}
          onSave={handleSave}
          onClose={handleCloseForm}
          isSaving={isSaving}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget !== null && (
        <DeleteConfirm
          artifactName={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}
